import { Button, MenuItem, Select, IconButton, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { SuccessCheckmarkIcon, DeleteIcon, EditIcon } from "../../Icons/Icons";


type SelectDropdownProps = {
  options: { label: string | number; value: string | number }[];
  selectedOption?: string | number;
  label?: string;
  className?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  hasCreateNewFilter?: boolean;
  hasEditableFilters?: boolean;
  createFilter?: (filter: string) => void;
  deleteFilter?: (filterId: string, filterValue: string) => void;
  editFilter?: (filterId: string, filterValue: string) => void;
  onChange: (event: any) => void;
};

const SelectDropDown = (props: SelectDropdownProps) => {
  const [showInput, setShowInput] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [open, setOpen] = useState(false);
  const hasInitialized = useRef(false);

  const {
    label,
    options,
    className,
    selectedOption,
    startAdornment,
    endAdornment,
    hasCreateNewFilter,
    hasEditableFilters,
    deleteFilter,
    createFilter,
    editFilter,
    onChange,
  } = props;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (hasInitialized.current) return;
    if (selectedOption === undefined && options.length > 0) {
      hasInitialized.current = true;
      onChangeRef.current({ target: { value: options[0].value, name: options[0].label } });
    }
  }, [selectedOption, options]);


  const handleClose = useCallback((event: any) => {
    try {
      const target = event.target as HTMLElement;
      if (target?.id === 'new-filter-button' ||
          target?.id === 'new-filter-textfield' ||
          target?.closest?.('.new-filter-textfield-container')
      ) {
        return;
      }
    } catch {
      // Defensive: on iOS Safari event.target may not support .closest()
    }
    setOpen(false);
    setShowInput(false);
  }, []);

  const handleOpen = () => {
    setOpen(true);
  }

  const handleDeleteItem = (event: any, item: any, label: any) => {
    event.stopPropagation()
    if (deleteFilter){
      deleteFilter(item, label);
    }
  }

  const handleEditItem = (event: any, item: any, label: any) => {
    event.stopPropagation()
    if (editFilter){
      editFilter(item, label)
    }
  }

  const handleSaveNewItem = (newItem: any) => {
    // In case it's a filter with adding functionality
    if (createFilter && newItem.trim()){
      createFilter(newItem.trim());
      setNewItem(""); // Clear input after saving
      setShowInput(false); // Hide input after saving
    }
  }
  
  const handleShowInput = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event from bubbling up
    setShowInput(!showInput);
  }

  const handleChange = (event: any) => {
    const selectedOption = options.find(opt => opt.value === event.target.value);
    const enrichedEvent = {
      target: {
        value: event.target.value,
        name: selectedOption?.label || '',
      }
    };
    onChange(enrichedEvent);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent dropdown from closing
    if (e.key === 'Enter' && newItem.trim()) {
      handleSaveNewItem(newItem.trim());
    }
  };

  const dropdownOptions = options.map(({ label, value }, index) => {
    // Don't show edit/delete icons for:
    // 1. The first item (index 0) - typically "Select [property]"
    // 2. Any item with value "Unlabeled" or label "Unlabeled" (case insensitive)
    const isUnlabeled = 
      typeof value === 'string' && value.toLowerCase() === 'unlabeled' ||
      typeof label === 'string' && label.toLowerCase() === 'unlabeled';
    
    if (index === 0 || isUnlabeled) {
      return (
        <MenuItem value={value} key={value} classes={{ selected: "selected" }}>
          {/* @ts-ignore */}
          <span title={label}>{label}</span>
        </MenuItem>
      );
    } else {
      return (
        <MenuItem value={value} key={value} classes={{ selected: "selected" }}>
          {/* @ts-ignore */}
          <span title={label}>{label}</span>
          {
            deleteFilter && 
            <div>
              {
                hasEditableFilters &&
                <Button onClick={(event) => handleEditItem(event, value, label)}>
                  <EditIcon/>
                </Button>
              }
              <Button onClick={(event) => handleDeleteItem(event, value, label)}>
                <DeleteIcon/>
              </Button>
            </div>
          }     
        </MenuItem>
      );
    }
  });
  
  if (hasCreateNewFilter){
    dropdownOptions.unshift(
      <MenuItem key="add-new-item">
        <Button 
          id="new-filter-button" 
          onClick={handleShowInput} 
          className="add-item"
          style={{ width: '100%', justifyContent: 'flex-start' }}
        >
          Add new item
        </Button>
      </MenuItem>
    )
  }

  return (
    <div className="flex drop-down-wrapper">
        <FormControl sx={{ minWidth: "100%" }} size="small" focused={false}>
          {label && <label htmlFor={label}>{label}</label>}
            <Select
              open={open}
              onOpen={handleOpen}
              onClose={handleClose}
              onChange={handleChange}
              value={selectedOption || (options.length > 0 ? options[0].value : "")}
              className={className + " select"}
              startAdornment={
                <span className="start-andorment">{startAdornment}</span>
              }
              endAdornment={<span className="end-andorment">{endAdornment}</span>}
              MenuProps={{
                classes: {
                  paper: "dropdown-paper",
                  list: "dropdown-list",
                },
                MenuListProps: { variant: "menu", disableListWrap: true },
              }}
              classes={{
                root: `custom-select ${
                  endAdornment || startAdornment ? "with-endorment" : ""
                }`,
              }}
            >
              {showInput && (
                <MenuItem key="new-item-input" className="new-filter-textfield-container">
                  <TextField 
                    id="new-filter-textfield" 
                    value={newItem} 
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    placeholder="Type and press Enter"
                    fullWidth
                  />
                  <IconButton size='small' onClick={() => handleSaveNewItem(newItem)}>
                    <SuccessCheckmarkIcon/>
                  </IconButton>
                </MenuItem>
              )}
              {dropdownOptions}
            </Select>        
        </FormControl>
    </div>
  );
};

export default SelectDropDown;
